import React, { createContext, useContext } from "react"
import { MobergColumn, MobergRow } from "../../../../../Moberg"
import { ConfigureWindowModalContext } from "../ConfigureWindowModal"
import { CompositePartTraceConfigJSON, RenderStrategy, SpectrogramTraceConfig, SpectrogramTraceConfigJSON, TraceConfigJSON, UpdateTraceOptions } from "../../../../../Pages/Data/Visualize/DataReview/Types/Trace"
import { ColorSpectrum } from "../../../../../Pages/Data/Visualize/DataReview/Types/ColorSpectrum"
import { applyModalitySpecificDefaults } from "../../../../../Constants/LabelSettings"
import { AnalyticSpectrogramModalities } from "./AnalyticSpectrogramModalities"
import { TabCount } from "../LineGraphModalities/TabCount"

type SpectrogramGraphModalitiesContextValue = {
	activeAnalyticSpectrogramTraces: SpectrogramTraceConfig[]
	handleColorSpectrumChange: (colorSpectrum: ColorSpectrum, traceConfig: SpectrogramTraceConfigJSON) => void
	handleValidationErrorChange: (key: string, error: string | undefined) => void
	handleDelete: (id: string) => void
	updateTrace: (traceId: string, config: Partial<SpectrogramTraceConfigJSON>, options?: UpdateTraceOptions) => void
}

export const SpectrogramGraphModalitiesContext = createContext<SpectrogramGraphModalitiesContextValue>({} as SpectrogramGraphModalitiesContextValue)

export const SpectrogramGraphModalities: React.FC = () => {
	const { activeAnalyticTraces, updateGraph, setValidationErrors, removeTrace } = useContext(ConfigureWindowModalContext)
	const tabs = ["Analytics"]
	const selectedTab = tabs[0]
	const activeAnalyticSpectrogramTraces = ((activeAnalyticTraces ?? []) as TraceConfigJSON[]).filter(
		trace => trace.renderStrategy === RenderStrategy.SPECTROGRAM
	) as SpectrogramTraceConfig[]

	const handleValidationErrorChange = (key: string, error: string | undefined) => {
		setValidationErrors(previous => {
			const copy = new Map(previous)

			if (copy.has(key) && error === undefined) {
				copy.delete(key)
			} else if (error !== undefined) {
				copy.set(key, error)
			}

			return copy
		})
	}

	const updateTrace = (traceId: string, config: Partial<SpectrogramTraceConfigJSON>, options?: UpdateTraceOptions) => {
		updateGraph((previousGraph: any) => {
			const defaults = { isVisible: true }
			const newTraces: TraceConfigJSON[] = (previousGraph.traces ?? []).map((trace: TraceConfigJSON) => {
				if (traceId === trace.id) {
					let newTrace = { ...defaults, ...trace, ...config, id: traceId }

					if (options?.useModalitySpecificDefaults) {
						newTrace = applyModalitySpecificDefaults(newTrace, newTrace.rawDataKey, RenderStrategy.SPECTROGRAM)
					}

					if ((config as CompositePartTraceConfigJSON).isCompositePart && (config as CompositePartTraceConfigJSON).compositeIndex !== undefined) {
						(newTrace as CompositePartTraceConfigJSON).compositeIndex = (config as CompositePartTraceConfigJSON).compositeIndex
					}

					return newTrace as TraceConfigJSON
				}

				return trace
			})

			return { ...previousGraph, traces: newTraces }
		})
	}

	const handleColorSpectrumChange = (colorSpectrum: ColorSpectrum, traceConfig: SpectrogramTraceConfigJSON) => {
		updateTrace(traceConfig.id, { ...traceConfig, colorSpectrum })
	}

	const handleDelete = (id: string) => {
		removeTrace(id)

		handleValidationErrorChange(`${id}-lowerBound`, undefined)
		handleValidationErrorChange(`${id}-upperBound`, undefined)
	}

	return (
		<MobergColumn gap="16px">
			<div style={{ borderBottom: "1px solid var(--color-border-light)" }}>
				<MobergRow gap="24px" style={{ padding: "0 4px" }}>
					{tabs.map(tab => (
						<div
							key={tab}
							style={{
								padding: "8px 4px",
								fontWeight: tab === selectedTab ? 600 : 400,
								color: tab === selectedTab ? "var(--color-accent-interactive)" : "var(--text-secondary-color)",
								borderBottom: tab === selectedTab ? "2px solid var(--color-accent-interactive)" : "2px solid transparent",
								fontSize: 14,
								background: "none",
								outline: "none",
								display: "flex",
								alignItems: "center",
								gap: "6px",
								marginBottom: "-1px",
							}}
						>
							<span>{tab}</span>
							<TabCount tab={tab} />
						</div>
					))}
				</MobergRow>
			</div>

			<SpectrogramGraphModalitiesContext.Provider
				value={{
					activeAnalyticSpectrogramTraces,
					handleDelete,
					handleColorSpectrumChange,
					handleValidationErrorChange,
					updateTrace,
				}}
			>
				<AnalyticSpectrogramModalities />
			</SpectrogramGraphModalitiesContext.Provider>
		</MobergColumn>
	)
}
